import React, { ReactElement, cloneElement, useEffect, useState } from 'react';

import { Flex, Group, Text, createStyles, useMantineTheme } from '@mantine/core';
import { Prism } from '@mantine/prism';
import { IconX } from '@tabler/icons-react';
import { ChatActionButton } from 'components/SmartAssistantComponents/ChatActionButton';
import { ChatLoader } from 'components/SmartAssistantComponents/ChatLoader';
import { Language } from 'prism-react-renderer';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { concatMap, delay, from, of, takeWhile } from 'rxjs';
import {
  ChatMessage,
  SmartAssistantMessageEventType,
  SmartAssistantPartialResponse
} from 'shared/interfaces/ChatMessage';
import { SmartAssistantVisualConfig } from 'shared/interfaces/SmartAssistant';
import { MPaper, MPaperProps } from 'shared/motion/MPaper';
import tinycolor from 'tinycolor2';

const DELAY_BETWEEN_UI_UPDATES_MS = 5;

export interface MessageBubbleProps extends MPaperProps {
  message?: ChatMessage;
  smartAssistantVisualConfig: SmartAssistantVisualConfig;
  onClose?: () => void;
  children?: ReactElement;
}

interface StyleParams {
  tableBorderColor: string;
  tableHeaderColor: string;
  textColor: string;
  fromUser: boolean;
  userMessageColor: string;
  assistantMessageColor: string;
  userTextColor: string;
  assistantTextColor: string;
}

const useStyles = createStyles(
  (
    theme,
    {
      tableBorderColor,
      tableHeaderColor,
      textColor,
      fromUser,
      userMessageColor,
      assistantMessageColor,
      userTextColor,
      assistantTextColor
    }: StyleParams
  ) => ({
    bubble: {
      padding: '8px 12px',
      borderRadius: '8px',
      fontSize: '0.875rem',
      color: fromUser ? userTextColor : assistantTextColor,
      backgroundColor: fromUser ? userMessageColor : assistantMessageColor,
      pointerEvents: 'auto'
    },
    markdownStyle: {
      overflowWrap: 'break-word',
      wordWrap: 'break-word',
      hyphens: 'auto',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '& > *': {
        margin: 0,
        fontSize: '0.875rem',
        color: textColor
      },
      '& table': {
        borderCollapse: 'collapse',
        width: '90%',
        margin: '8px auto'
      },
      '& th, & td': {
        border: `1px solid ${tableBorderColor}`,
        padding: theme.spacing.md,
        textAlign: 'left'
      },
      '& th': {
        backgroundColor: tableHeaderColor
      },
      '& p': {
        whiteSpace: 'pre-line',
        marginTop: '10px',
        marginBottom: '10px'
      },
      '& p:first-of-type': {
        marginTop: '0'
      },
      '& p:last-of-type': {
        marginBottom: '0'
      },
      '& li': {
        marginBottom: '5px'
      },
      '& ul li': {
        marginBottom: 0
      },
      '& a': {
        wordBreak: 'break-all',
        color: textColor
      },
      // Hide images in markdown since we aren't handling sizes
      '& img': {
        display: 'none'
      }
    }
  })
);

const KNOWN_CODE_LANGUAGES = new Set([
  'markup',
  'clike',
  'bash',
  'c',
  'cpp',
  'css',
  'javascript',
  'jsx',
  'coffeescript',
  'actionscript',
  'diff',
  'css-extr',
  'git',
  'go',
  'graphql',
  'handlebars',
  'json',
  'less',
  'makefile',
  'markdown',
  'objectivec',
  'ocaml',
  'python',
  'reason',
  'sass',
  'scss',
  'sql',
  'stylus',
  'tsx',
  'typescript',
  'wasm',
  'yaml'
]);

export function MessageBubble({
  message,
  children,
  onClose,
  smartAssistantVisualConfig,
  ...paperProps
}: MessageBubbleProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { defaultLight: lightTextColor } = theme.other.text;

  const [messageText, setMessageText] = useState(message?.text ?? '');
  const {
    userMessageColor,
    assistantMessageColor,
    conversationBackgroundColor,
    assistantAvatar,
    assistantName,
    showBadge,
    assistantBadgeTitle = t('SMART_ASSISTANT.BADGE_TITLE.AI_AGENT')
  } = smartAssistantVisualConfig;

  const userTextColor = theme.other.getReadableColor(userMessageColor);
  const assistantTextColor = theme.other.getReadableColor(assistantMessageColor);
  const assistantBadgeColor = conversationBackgroundColor;
  const assistantBadgeTextColor = tinycolor(theme.other.getReadableColor(assistantBadgeColor))
    .lighten(10)
    .toHexString();

  const { classes } = useStyles({
    tableBorderColor: message?.fromUser ? userTextColor : assistantTextColor,
    tableHeaderColor: message?.fromUser
      ? tinycolor.mix(userMessageColor, lightTextColor, 50).toString()
      : tinycolor.mix(assistantMessageColor, lightTextColor, 50).toString(),
    textColor: message?.fromUser ? userTextColor : assistantTextColor,
    fromUser: message?.fromUser ?? false,
    userMessageColor,
    assistantMessageColor,
    userTextColor,
    assistantTextColor
  });

  useEffect(() => {
    if (!message?.partialResponseSubject || messageText) return;
    const subscription = message.partialResponseSubject
      .pipe(
        takeWhile(
          // We expect to receive the stream end event to complete the subscription
          (partialResponseEvent) => partialResponseEvent.type === SmartAssistantMessageEventType.PARTIAL_RESPONSE
        ),
        concatMap((partialResponseEvent) =>
          from((partialResponseEvent.data as SmartAssistantPartialResponse).text).pipe(
            concatMap((character) => of(character).pipe(delay(DELAY_BETWEEN_UI_UPDATES_MS)))
          )
        )
      )
      .subscribe({
        next: (character) => setMessageText((prevMessageText) => prevMessageText + character)
      });

    return () => subscription.unsubscribe();
  }, [message?.partialResponseSubject]);

  useEffect(() => {
    if (message?.text) {
      setMessageText(message.text);
    }
  }, [message?.text]);

  const markdownCustomComponents = {
    // Since we do no handle images in markdown, we replace them with links
    img: ({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) => (
      <a target="_blank" href={src} rel="noopener noreferrer">
        {alt || ''}
      </a>
    ),
    a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
      <a target="_blank" rel="noopener noreferrer" {...props} />
    ),
    code({ className, children, ...props }: React.HTMLAttributes<HTMLElement>) {
      const match = /language-(\w+)/.exec(className || '');
      const codeLanguage = match && KNOWN_CODE_LANGUAGES.has(match[1]) ? match[1] : undefined;

      return codeLanguage ? (
        <Prism
          language={codeLanguage as Language}
          colorScheme={
            tinycolor(message?.fromUser ? userMessageColor : assistantMessageColor).isDark() ? 'dark' : 'light'
          }
          withLineNumbers
          style={{ marginTop: '8px', marginBottom: '8px' }}
          copyLabel={t('SMART_ASSISTANT.COPY_CODE_SNIPPET')}
          copiedLabel={t('SMART_ASSISTANT.CODE_SNIPPET_COPIED')}
          {...props}>
          {String(children).replace(/\n$/, '')}
        </Prism>
      ) : (
        <code
          className={className}
          {...props}
          style={{
            fontSize: '0.95rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
          {children}
        </code>
      );
    }
  };

  const preprocessLaTeX = (content: string) => {
    // Replace block-level LaTeX delimiters \[ \] with $$ $$
    const blockProcessedContent = content.replace(/\\\[([\s\S]*?)\\\]/g, (_, equation) => `$$${equation}$$`);

    // Replace inline LaTeX delimiters \( \) with $$ $$
    const inlineProcessedContent = blockProcessedContent.replace(
      /\\\(([\s\S]*?)\\\)/g,
      (_, equation) => `$$${equation}$$`
    );
    return inlineProcessedContent;
  };

  const TextContent = () => (
    <Markdown
      remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }]]}
      rehypePlugins={[rehypeKatex]}
      className={classes.markdownStyle}
      components={markdownCustomComponents}>
      {preprocessLaTeX(messageText)}
    </Markdown>
  );

  const SmartAssistantMessage = ({ loading = false }: { loading?: boolean }) => (
    <>
      <Flex pt={6} pb={8} justify="space-between" align="center">
        <Flex gap={7} align="center">
          {!!assistantAvatar &&
            cloneElement(assistantAvatar, {
              size: 14,
              radius: 15
            })}
          {loading && (
            <ChatLoader isLight={tinycolor(message?.fromUser ? userMessageColor : assistantMessageColor).isDark()} />
          )}
          {assistantName && !loading && <Text fw={600}>{assistantName}</Text>}
        </Flex>
        {onClose ? (
          <ChatActionButton bg={assistantMessageColor} onClick={onClose}>
            <IconX color={assistantTextColor} style={{ strokeWidth: 0.7 }} />
          </ChatActionButton>
        ) : (
          showBadge && (
            <Flex
              align="center"
              justify="center"
              py={4}
              px={8}
              ml={15}
              style={{
                borderRadius: '28px',
                fontSize: '0.625rem',
                color: assistantBadgeTextColor
              }}
              bg={assistantBadgeColor}>
              {assistantBadgeTitle}
            </Flex>
          )
        )}
      </Flex>
      <TextContent />
    </>
  );

  const UserMessage = () => <TextContent />;

  return (
    <Group position={message?.fromUser ? 'right' : 'left'} my="sm">
      <MPaper className={classes.bubble} {...paperProps}>
        {message?.fromUser ? (
          <UserMessage />
        ) : (
          <SmartAssistantMessage loading={message?.partialResponseSubject && messageText === ''} />
        )}
        {children ? <>{children}</> : null}
      </MPaper>
    </Group>
  );
}
